import { useMemo } from 'react';
import useClinicUser from 'shared/hooks/useClinicUser';
import { useBitwerxData } from '../providers/IntegrationsProvider/hooks/useBitwerxData';
import {
  useGetBitwerxPaymentWritebackConfigQuery,
  useGetPimsPaymentTypesQuery,
  useGetClinicEmployeesForPaymentSettingsQuery,
} from 'shared/types/graphql';
import useFeatureFlag from './useFeatureFlag';
import { FeatureFlagName } from 'shared/enums';
import { integrationSystemDisplayName, useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';

interface PaymentWritebackConfigResponse {
  isValid: boolean;
  isComplete: boolean;
  isClinicWritebackEnabled: boolean;
  isLoading: boolean;
  isWritebackManualPaymentEnabled: boolean;
}

const usePaymentWritebackConfig = (): PaymentWritebackConfigResponse => {
  const { primaryIntegrationName } = useIntegrationsProvider();
  const { bitwerxIntegrationId, hasBitwerx, writebackCapable } = useBitwerxData();
  const { currentClinicId } = useClinicUser();

  const { isFeatureEnabled } = useFeatureFlag();

  // CS Test Clinic is the only CS Datapoint clinic where writebacks work, so add exception for it
  const isCSTestClinic = useMemo(() => currentClinicId === 'ck7535bgm0r30070906k3bvi5', [currentClinicId]);

  // only allow writeback config for AVI/CS clinics or Impromed clinics with the BitwerxPaymentWritebacks FF enabled
  const isClinicWritebackEnabled = useMemo(
    () =>
      writebackCapable ||
      isCSTestClinic ||
      (isFeatureEnabled(FeatureFlagName.BitwerxPaymentWritebacks) &&
        primaryIntegrationName === integrationSystemDisplayName.IMPROMED),
    [writebackCapable, isCSTestClinic, isFeatureEnabled, primaryIntegrationName],
  );

  const { data: bitwerxPaymentCofigData, loading: loadingConfigData } = useGetBitwerxPaymentWritebackConfigQuery({
    variables: {
      integrationId: bitwerxIntegrationId || '',
    },
    skip: !bitwerxIntegrationId,
  });

  const { data: paymentTypesData, loading: loadingPaymentTypesData } = useGetPimsPaymentTypesQuery({
    variables: { integrationId: bitwerxIntegrationId || '' },
    skip: !bitwerxIntegrationId,
  });

  const { data: clinicEmployeeData, loading: loadingEmployeesData } = useGetClinicEmployeesForPaymentSettingsQuery({
    variables: { clinicId: currentClinicId || '' },
    skip: !currentClinicId,
  });

  const isLoading = useMemo(
    () => loadingConfigData || loadingEmployeesData || loadingPaymentTypesData,
    [loadingConfigData, loadingEmployeesData, loadingPaymentTypesData],
  );

  // if part of required config is incomplete
  const writebackConfigIncomplete = useMemo(() => {
    const writebackConfig = bitwerxPaymentCofigData?.findUniqueBitwerxPaymentWritebackConfiguration;
    return hasBitwerx && (!writebackConfig?.defaultPimsPaymentTypeId || !writebackConfig?.enteredById);
  }, [bitwerxPaymentCofigData?.findUniqueBitwerxPaymentWritebackConfiguration, hasBitwerx]);

  // if a stored payment type or employee id corresponds to a deleted PIMS entity
  const writebackConfigInvalid = useMemo(() => {
    const isPaymentTypeDeleted = (id: string): boolean => {
      return !!paymentTypesData?.findManyPimsPaymentType?.find((type) => type.id === id)?.isDeleted;
    };

    let hasInvalidField = false;

    const writebackConfig = bitwerxPaymentCofigData?.findUniqueBitwerxPaymentWritebackConfiguration;
    const paymentTypeIds = [
      writebackConfig?.defaultPimsPaymentTypeId,
      writebackConfig?.amexPimsPaymentTypeId,
      writebackConfig?.discoverPimsPaymentTypeId,
      writebackConfig?.mastercardPimsPaymentTypeId,
      writebackConfig?.visaPimsPaymentTypeId,
    ];

    if (writebackConfig?.enteredById) {
      const employee = clinicEmployeeData?.findManyClinicEmployee?.find(
        (employee) => employee.id === writebackConfig.enteredById,
      );
      if (employee?.isDeleted) {
        hasInvalidField = true;
      }
    }

    paymentTypeIds.forEach((id) => {
      if (id && isPaymentTypeDeleted(id)) {
        hasInvalidField = true;
      }
    });

    return hasInvalidField;
  }, [
    bitwerxPaymentCofigData?.findUniqueBitwerxPaymentWritebackConfiguration,
    clinicEmployeeData?.findManyClinicEmployee,
    paymentTypesData?.findManyPimsPaymentType,
  ]);

  const isWritebackManualPaymentEnabled = useMemo(
    () => !!bitwerxPaymentCofigData?.findUniqueBitwerxPaymentWritebackConfiguration?.writebackManualPayment,
    [bitwerxPaymentCofigData?.findUniqueBitwerxPaymentWritebackConfiguration?.writebackManualPayment],
  );

  return {
    isValid: !writebackConfigInvalid,
    isComplete: !writebackConfigIncomplete,
    isClinicWritebackEnabled,
    isLoading,
    isWritebackManualPaymentEnabled,
  };
};

export default usePaymentWritebackConfig;
